import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Tooltip,
  TextField,
  InputAdornment,
  Grid,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import ForumIcon from "@mui/icons-material/Forum";
import { useQuery } from "@tanstack/react-query";
import HttpService from "../../services/HttpService";
import SupportChatConversationDialog from "./SupportChatConversationDialog";
import CardStatsElement from "../../components/CardStatsElement";

async function fetchAllConversations() {
  const res = await HttpService.getAxiosClient().get(
    window.API_URL + "/chat/support/conversations/all"
  );
  return res.data;
}

const STATUS_COLORS = {
  ESCALATED: "warning",
  ESCALATED_CALL: "error",
  HUMAN: "success",
  CLOSED: "default",
  BOT: "info",
};

function statusChip(status) {
  const label =
    status === "BOT"
      ? "Assistant"
      : status === "HUMAN"
        ? "Agent"
        : status === "ESCALATED_CALL"
          ? "Escalated Call"
          : status;
  return (
    <Chip
      label={label}
      color={STATUS_COLORS[status] || "default"}
      size="small"
    />
  );
}

export default function SupportChatAllConversations() {
  const [selectedConversationId, setSelectedConversationId] = useState(null);
  const [search, setSearch] = useState("");

  const {
    data: conversations,
    isFetching,
    isError,
    refetch,
  } = useQuery(["supportAllConversations"], fetchAllConversations, {
    refetchOnWindowFocus: false,
  });

  const filtered = (conversations || []).filter((c) => {
    const q = search.toLowerCase();
    return (
      !q ||
      c.id?.toLowerCase().includes(q) ||
      c.ownerId?.toLowerCase().includes(q) ||
      c.guestRef?.toLowerCase().includes(q) ||
      c.assignedAgentId?.toLowerCase().includes(q) ||
      c.status?.toLowerCase().includes(q) ||
      c.customerFirstName?.toLowerCase().includes(q) ||
      c.customerLastName?.toLowerCase().includes(q) ||
      c.customerEmail?.toLowerCase().includes(q)
    );
  });

  return (
    <Box p={3}>
      {/* Header */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={3}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <ForumIcon sx={{ color: "#0d7f81", fontSize: 28 }} />
          <Typography variant="h6" fontWeight="bold">
            All Conversations
          </Typography>
          {isFetching && <CircularProgress size={18} sx={{ ml: 1 }} />}
        </Box>
        <Tooltip title="Refresh">
          <span>
            <IconButton onClick={() => refetch()} disabled={isFetching}>
              <RefreshIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load conversations.
        </Alert>
      )}

      {/* Statistics */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <CardStatsElement
          isLoadingStats={isFetching}
          isErrorStats={isError}
          text="Total Conversations"
          data={(conversations || []).length}
          md={2}
          countFontSize={45}
          headerTypograhy="body1"
        />
        <CardStatsElement
          isLoadingStats={isFetching}
          isErrorStats={isError}
          text="Assistant"
          data={(conversations || []).filter((c) => c.status === "BOT").length}
          md={2}
          countFontSize={45}
          headerTypograhy="body1"
        />
        <CardStatsElement
          isLoadingStats={isFetching}
          isErrorStats={isError}
          text="Escalated"
          data={
            (conversations || []).filter((c) => c.status === "ESCALATED").length
          }
          md={2}
          countFontSize={45}
          headerTypograhy="body1"
        />
        <CardStatsElement
          isLoadingStats={isFetching}
          isErrorStats={isError}
          text="With Agent"
          data={
            (conversations || []).filter((c) => c.status === "HUMAN").length
          }
          md={2}
          countFontSize={45}
          headerTypograhy="body1"
        />
        <CardStatsElement
          isLoadingStats={isFetching}
          isErrorStats={isError}
          text="Closed"
          data={
            (conversations || []).filter((c) => c.status === "CLOSED").length
          }
          md={2}
          countFontSize={45}
          headerTypograhy="body1"
        />
        <CardStatsElement
          isLoadingStats={isFetching}
          isErrorStats={isError}
          text="Closed Today"
          data={
            (conversations || []).filter((c) => {
              if (c.status !== "CLOSED" || !c.updatedAt) return false;
              const today = new Date();
              const d = new Date(c.updatedAt);
              return (
                d.getFullYear() === today.getFullYear() &&
                d.getMonth() === today.getMonth() &&
                d.getDate() === today.getDate()
              );
            }).length
          }
          md={2}
          countFontSize={45}
          headerTypograhy="body1"
        />
      </Grid>

      {/* Search */}
      <TextField
        size="small"
        placeholder="Search by ID, member, agent, status…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ mb: 2, width: 360 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
      />

      <Paper elevation={2}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: "#0d7f81" }}>
              {[
                "Conversation ID",
                "Status",
                "Member / Guest",
                "Assigned Agent",
                "Created",
                "Last Updated",
              ].map((h) => (
                <TableCell
                  key={h}
                  sx={{ color: "white", fontWeight: "bold", fontSize: 13 }}
                >
                  {h}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={6}
                  align="center"
                  sx={{ py: 4, color: "text.secondary" }}
                >
                  {isFetching ? "Loading…" : "No conversations found."}
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((conv) => (
                <TableRow
                  key={conv.id}
                  hover
                  sx={{ cursor: "pointer" }}
                  onClick={() => setSelectedConversationId(conv.id)}
                >
                  <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>
                    {conv.id}
                  </TableCell>
                  <TableCell>{statusChip(conv.status)}</TableCell>
                  <TableCell>
                    {conv.customerFirstName ? (
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {conv.customerFirstName} {conv.customerLastName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {conv.customerEmail}
                        </Typography>
                      </Box>
                    ) : (
                      conv.guestRef || conv.ownerId || "—"
                    )}
                  </TableCell>
                  <TableCell>
                    {conv.assignedAgentId || (
                      <em style={{ color: "#999" }}>Unassigned</em>
                    )}
                  </TableCell>
                  <TableCell>
                    {conv.createdAt
                      ? new Date(conv.createdAt).toLocaleString()
                      : "—"}
                  </TableCell>
                  <TableCell>
                    {conv.updatedAt
                      ? new Date(conv.updatedAt).toLocaleString()
                      : "—"}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Paper>
      <SupportChatConversationDialog
        conversationId={selectedConversationId}
        onClose={() => setSelectedConversationId(null)}
      />
    </Box>
  );
}
